import Bro from 'brototype';
import moment from 'moment';

import ListoryStore, { StoreContent } from '@/store';
import { HomeContent, OnBoardingData, OnBoardingsState, NewsEntry } from '@/models/home';
import { getRandomInt } from '@/utils';
import ProgressCalculator from '@/services/progress-calculator';
import { TranslateResult } from 'vue-i18n';
import { DefaultOnBoardingPoi } from '@/constants';

export class HomeHelper {
    public static GetOnBoardings(store: ListoryStore): OnBoardingData[] {
        const t = HomeHelper.getTranslator(store);
        const tc = HomeHelper.getTranslator(store);

        return [
            // Card 0 -> is displayed on the first start, after that it stays until the user hides it
            // Card 1-3 -> is displayed when Card 0-3 was closed, the next one will be displayed after one day
            // Card 4 -> DISPLAY RANDOM WELCOME MESSAGE all 5 days
            // Card X -> is showed when all pois were unlocked
            OnBoardingData.Create(
                OnBoardingsState.FIRST_START,
                t('onboarding.first_start_title'),
                t('onboarding.first_start_text'),
                DefaultOnBoardingPoi
            ),
            OnBoardingData.Create(
                OnBoardingsState.SECOND_START,
                t('onboarding.second_start_title'),
                t('onboarding.second_start_text')
            ),
            OnBoardingData.Create(
                OnBoardingsState.THIRD_START,
                t('onboarding.third_start_title'),
                t('onboarding.third_start_text')
            ),
            OnBoardingData.Create(
                OnBoardingsState.FOURTH_START,
                t('onboarding.fourth_start_title'),
                t('onboarding.fourth_start_text')
            ),

            OnBoardingData.Create(
                OnBoardingsState.RANDOM_WELCOME_MESSAGE,
                t('onboarding.random_1_title'),
                t('onboarding.random_1_text')
            ),
            OnBoardingData.Create(
                OnBoardingsState.RANDOM_WELCOME_MESSAGE,
                t('onboarding.random_2_title'),
                t('onboarding.random_2_text')
            ),
            OnBoardingData.Create(
                OnBoardingsState.RANDOM_WELCOME_MESSAGE,
                t('onboarding.random_3_title'),
                t('onboarding.random_3_text')
            ),
            OnBoardingData.Create(
                OnBoardingsState.RANDOM_WELCOME_MESSAGE,
                t('onboarding.random_4_title'),
                t('onboarding.random_4_text')
            ),

            // add as much as you want!!!!!!!!!!!!!!!!!

            OnBoardingData.Create(
                OnBoardingsState.EVERYTHING_IN_THE_APP_WAS_ACHIEVED,
                t('onboarding.finisher_title'),
                t('onboarding.finisher_text')
            )
        ];
    }

    public static GetHomeContent(store: ListoryStore): HomeContent {
        const homeContent = new HomeContent();
        const t = HomeHelper.getTranslator(store);

        homeContent.arCardTitle = t('poi_ar.title_plural');
        homeContent.routeCardTitle = t('home.routes_title', { count: store.routes.length });
        homeContent.poiCardTitle = t('home.pois_title', { count: store.pois.length });

        // get random route image
        const randomRouteIndex = getRandomInt(store.routes.length);
        if (Bro(store.routes[randomRouteIndex]).doYouEven('images.0.image.medium') === Bro.TOTALLY) {
            homeContent.routeCardImage = store.routes[randomRouteIndex].images[0].image.medium;
        }

        // get random poi image
        const randomPoiIndex = getRandomInt(store.pois.length);
        if (Bro(store.pois[randomPoiIndex]).doYouEven('content_images.0.image.medium') === Bro.TOTALLY) {
            homeContent.poiCardImage = store.pois[randomPoiIndex].content_images[0].image.medium;
        }

        // get random ar poi image
        const arPois = store.pois.filter(poi => poi.is_special);
        homeContent.arCardTitle = t('poi_ar.title_plural', { count: arPois.length });

        if (arPois.length > 0) {
            const randomArPoiIndex = getRandomInt(arPois.length);
            if (Bro(arPois[randomArPoiIndex]).doYouEven('content_ar_experiences.0.preview.medium') === Bro.TOTALLY) {
                homeContent.arCardImage = arPois[randomArPoiIndex].content_ar_experiences[0].preview.medium;
            }
        }

        return homeContent;
    }

    public static UpdateHasFavorites(store: ListoryStore, homeContent: HomeContent) {

        if (!homeContent) return;

        const tc = HomeHelper.getCountableTranslator(store);

        homeContent.hasFavorites = store.poiFavorites && store.poiFavorites.length > 0;
        if (homeContent.hasFavorites) {
            const firstFavoritePoiId = store.poiFavorites[0];
            const firstFavoritePoi = store.pois.find(p => p.id === firstFavoritePoiId);
            if (Bro(firstFavoritePoi).doYouEven('content_images.0.image.medium') === Bro.TOTALLY) {
                homeContent.favoriteCardImage = firstFavoritePoi.content_images[0].image.medium;
            }

            homeContent.favoriteCardTitle = tc('home.favorites_title', store.poiFavorites.length, { count: store.poiFavorites.length });
        }
    }

    public static GetOnBoardingProgress(store: ListoryStore) {
        return store.onBoardingProgress;
    }


    public static GetOnBoardingData(store: ListoryStore) {
        const showRandomWelcomeMessage = store.onBoardingProgress.state === OnBoardingsState.RANDOM_WELCOME_MESSAGE;
        const userHasAlreadySeenMessage = !store.onBoardingProgress.userHasClickedAway;
        const onBoardings = HomeHelper.GetOnBoardings(store);

        if (showRandomWelcomeMessage && userHasAlreadySeenMessage) {
            const randomMessagePredicate = b => b.state === OnBoardingsState.RANDOM_WELCOME_MESSAGE;
            const randomWelcomeMessages = onBoardings.filter(randomMessagePredicate);
            const randomWelcomeMessageIndex = getRandomInt(randomWelcomeMessages.length);
            return randomWelcomeMessages[randomWelcomeMessageIndex];
        } else {
            return onBoardings.find(
                b => b.state === store.onBoardingProgress.state && !store.onBoardingProgress.userHasClickedAway
            );
        }
    }

    public static async IncrementOnBoardingState(store: StoreContent) {
        const progress = new ProgressCalculator(store);
        const past = moment(store.onBoardingProgress.date);
        const now = moment();

        if (store.pois.length > 0 && progress.unlockedPoiCount === store.pois.length) {
            store.onBoardingProgress = {
                state: OnBoardingsState.EVERYTHING_IN_THE_APP_WAS_ACHIEVED,
                date: moment(),
                userHasClickedAway: false
            };
        } else {
            switch (store.onBoardingProgress.state) {
                case OnBoardingsState.FIRST_START:
                    if (now.diff(past, 'days') >= 1)
                        store.onBoardingProgress = {
                            state: OnBoardingsState.SECOND_START,
                            date: now,
                            userHasClickedAway: false
                        };
                    break;
                case OnBoardingsState.SECOND_START:
                    if (now.diff(past, 'days') >= 1)
                        store.onBoardingProgress = {
                            state: OnBoardingsState.THIRD_START,
                            date: now,
                            userHasClickedAway: false
                        };
                    break;
                case OnBoardingsState.THIRD_START:
                    if (now.diff(past, 'days') >= 1)
                        store.onBoardingProgress = {
                            state: OnBoardingsState.FOURTH_START,
                            date: now,
                            userHasClickedAway: false
                        };
                    break;
                case OnBoardingsState.FOURTH_START:
                    if (now.diff(past, 'days') >= 1)
                        store.onBoardingProgress = {
                            state: OnBoardingsState.RANDOM_WELCOME_MESSAGE,
                            date: now,
                            userHasClickedAway: false
                        };
                    break;
                case OnBoardingsState.RANDOM_WELCOME_MESSAGE:
                    if (now.diff(past, 'days') >= 5)
                        store.onBoardingProgress = {
                            state: OnBoardingsState.RANDOM_WELCOME_MESSAGE,
                            date: now,
                            userHasClickedAway: false
                        };
                    break;
                case OnBoardingsState.EVERYTHING_IN_THE_APP_WAS_ACHIEVED:
                    // done, du hast dir ein Gipfeli verident
                    break;
            }
        }

        await store.saveOnBoardingProgress();
    }

    // only for unitTests
    public static async SetOnBoardingProgress(
        store: StoreContent,
        state: OnBoardingsState,
        date: moment.Moment,
        userHasClickedAway: boolean
    ) {
        store.onBoardingProgress = { state: state, date: date, userHasClickedAway: userHasClickedAway };
    }

    private static getTranslator(store: ListoryStore) {
        let t = (key: string, values?: any) => key as TranslateResult;
        if (store.$t) t = store.$t.bind(store);
        return t;
    }

    private static getCountableTranslator(store: ListoryStore) {
        let tc = (key: string, choice: number, values?: any) => key as TranslateResult;
        if (store.$tc) tc = store.$tc.bind(store);
        return tc;
    }
}
